// src/components/PodcastCard.jsx
import { motion } from 'framer-motion'
import { Clock, Headphones, BookOpen, Calendar, Music, AlertCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import useStore from '../store/useStore'
import { useState } from 'react'
import { hasAudioFile } from '../utils/audioFolder'

function PodcastCard({ podcast }) {
  const navigate = useNavigate()
  const { setCurrentlyPlaying, user } = useStore()
  const [isHovered, setIsHovered] = useState(false)
  
  const hasAudio = hasAudioFile(podcast.audioUrl?.split('/').pop() || '')
  
  const episodeTypeColors = {
    'Critique': 'bg-red-100 text-red-700',
    'Tutorial': 'bg-green-100 text-green-700',
    'Deep Dive': 'bg-purple-100 text-purple-700',
    'Debate': 'bg-orange-100 text-orange-700',
    'Educational': 'bg-blue-100 text-blue-700',
    'Investigation': 'bg-cyan-100 text-cyan-700',
    'Analysis': 'bg-indigo-100 text-indigo-700',
    'Documentary': 'bg-rose-100 text-rose-700',
    'Explainer': 'bg-teal-100 text-teal-700'
  }

  const handlePlay = () => {
    if (!user) {
      localStorage.setItem('lastVisitedPage', '/podcasts')
      navigate('/auth')
      return
    }
    
    if (!hasAudio) {
      navigate(`/podcast/coming-soon/${podcast.id}`, { 
        state: { 
          podcast,
          missingAudio: true 
        } 
      })
      return
    }
    
    setCurrentlyPlaying(podcast)
  }

  const handleCourseNavigation = () => {
    if (!user) {
      localStorage.setItem('lastVisitedPage', '/podcasts')
      navigate('/auth')
      return
    }
    navigate(`/course/${podcast.relatedCourseId}`)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getAudioStatusBadge = () => {
    if (hasAudio) {
      return (
        <div className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1 border border-green-300">
          <Music className="w-3 h-3" />
          Ready
        </div>
      )
    } else {
      return (
        <div className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1 border border-amber-300">
          <AlertCircle className="w-3 h-3" />
          Coming Soon
        </div>
      )
    }
  }

  const getButtonText = () => {
    if (hasAudio) return "Listen Now"
    return "Preview Details"
  }

  const getButtonColor = () => {
    if (hasAudio) return "bg-blue-500 hover:bg-blue-600"
    return "bg-amber-500 hover:bg-amber-600"
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col min-h-[480px] group relative"
    >
      {/* Red dot for missing audio (only for your reference) */}
      {!hasAudio && (
        <div className="absolute top-2 left-2 z-10">
          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" title="Missing audio file"></div>
        </div>
      )}
      
      {/* Podcast Image */}
      <div className="relative h-48 bg-gray-200 flex-shrink-0">
        <img 
          src={podcast.image} 
          alt={podcast.title}
          className="w-full h-full object-cover"
        />
        
        {/* Badges - Modified: Removed tier badge, moved audio status to top right */}
        <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${episodeTypeColors[podcast.episodeType]} flex-shrink-0`}>
            {podcast.episodeType}
          </span>
          
          {/* Audio Status Badge moved to top right (where tier badge was) */}
          {getAudioStatusBadge()}
        </div>
        
        {/* File name on hover (for debugging) */}
        {!hasAudio && (
          <div 
            className="absolute bottom-4 right-4 bg-black/70 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            title="Audio file needed"
          >
            {podcast.audioUrl?.split('/').pop() || 'No audio'}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col flex-1">
        {/* Topic */}
        <div className="mb-4 flex-shrink-0 flex justify-center">
          <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
            {podcast.topic}
          </span>
        </div>

        {/* Title */}
        <div className="mb-3 flex-shrink-0">
          <h3 
            className="text-xl font-bold text-gray-900 leading-tight text-center cursor-help"
            title={podcast.title}
          >
            {podcast.title.length > 60 && !isHovered ? `${podcast.title.substring(0, 60)}...` : podcast.title}
          </h3>
        </div>

        {/* Summary */}
        <div className="flex-1 mb-6 min-h-0">
          <div 
            className={`text-gray-600 text-sm leading-relaxed transition-all duration-300 text-center ${
              isHovered ? 'line-clamp-none' : 'line-clamp-4'
            }`}
          >
            {podcast.summary}
          </div>
          {!isHovered && podcast.summary.length > 150 && (
            <div className="text-xs text-gray-400 mt-2 text-center">Hover to read more</div>
          )}
        </div>

        {/* Metadata */}
        <div className="flex items-center justify-between text-xs text-gray-500 mb-6 flex-shrink-0 space-x-2">
          <div className="flex items-center space-x-1.5 min-w-0 flex-1 justify-center">
            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{podcast.duration}</span>
          </div>
          
          <div className="flex items-center space-x-1.5 min-w-0 flex-1 justify-center">
            <Headphones className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{podcast.listens?.toLocaleString() || '0'}</span>
          </div>
          
          <div className="flex items-center space-x-1.5 min-w-0 flex-1 justify-center">
            <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{formatDate(podcast.publishDate)}</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3 mt-auto pt-4 flex-shrink-0">
          <button
            onClick={handlePlay}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 ${getButtonColor()} text-white rounded-lg font-medium transition-colors duration-200`}
          >
            <Headphones className="w-5 h-5" />
            <span>{getButtonText()}</span>
          </button>
          {podcast.relatedCourseId && (
            <button
              onClick={handleCourseNavigation}
              className="flex items-center justify-center px-4 py-3 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-600 transition-colors duration-200 flex-shrink-0"
              title="View Related Course"
            >
              <BookOpen className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  )
}

export default PodcastCard